/**
 * The import queue on screen (W2-09 publish-import; DESIGN.md §5.12 "Library progress, one summary report"): a
 * status item of the 'banner' slot, mounted in the Library and in the editor.
 *
 *  - While imports run: "Importing 2 of 5: Song.pez", progress and Cancel (in the Library a single import shows only
 *    the Library's own progress).
 *  - After a run of several: "Import finished: 3 new projects, 1 file not imported" with View report / Dismiss.
 *  - The queue's own dialogs, the batch-export picker and the summary report (every import with its outcome, Open,
 *    its report, Copy / Save report, and an area to import more), are an always-mounted overlay of the module
 *    (`ImportQueueDialogs`, FeatureModule.overlays, CR-W2-09-2), so they show on every route.
 */

import { useEffect, useState } from 'react';
import type { AppServices, IconName } from '../../app/registry/types';
import { Button } from '../../ui/Button';
import { Dialog } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import { ProgressBar } from '../../ui/ProgressBar';
import { BatchPicker } from './BatchPicker';
import { DropZone } from './DropZone';
import { useQueue, useStore, useText, type Text } from './hooks';
import { m } from './import.messages';
import { classCounts, reportSummary, reportText } from './importFlow';
import { createdCount, failedCount, importQueue, startWizard, type Outcome, type QueueSnapshot } from './importQueue';
import { ReportActions, ReportView } from './ImportReportDialog';
import s from './Import.module.css';

const OUTCOME_ICONS: Readonly<Record<Outcome['kind'], IconName>> = {
  project: 'FilePlus2',
  merged: 'Merge',
  skin: 'Palette',
  batch: 'Archive',
  media: 'Music',
  cancelled: 'CircleSlash',
  failed: 'CircleX',
};

/** The headline of an outcome. */
export function outcomeTitle(text: Text, o: Outcome): string {
  switch (o.kind) {
    case 'project':
      return text.t(m.outcomeProject, { title: o.title });
    case 'merged':
      return text.t(m.outcomeMerged, { lines: o.lines, name: o.report.fileName });
    case 'skin':
      return text.t(o.installedId !== null ? m.outcomeSkin : m.outcomeSkinSkipped, { name: o.name });
    case 'batch':
      return text.t(m.outcomeBatch, { picked: o.picked, total: o.total });
    case 'media':
      return o.started ? text.t(m.outcomeMediaStarted) : text.t(m.outcomeMedia, { count: o.assets.length });
    case 'cancelled':
      return text.t(m.outcomeCancelled);
    case 'failed':
      return text.t(m.outcomeFailed, { error: text.tr(o.error) });
  }
}

/** The whole run as plain text (Copy / Save report of the summary). */
export function summaryText(text: Text, outcomes: readonly Outcome[]): string {
  const blocks: string[] = [text.t(m.summaryTitle), text.t(m.summaryIntro, { count: outcomes.length })];
  for (const o of outcomes) {
    const lines = [`${o.job.name}: ${outcomeTitle(text, o)}`];
    if (o.kind === 'failed' && o.detail !== undefined) lines.push(o.detail);
    if (o.kind === 'project' || o.kind === 'merged') lines.push(reportText(text, o.report, o.missing));
    blocks.push(lines.join('\n'));
  }
  return blocks.join('\n\n');
}

function OutcomeRow(props: { readonly services: AppServices; readonly text: Text; readonly outcome: Outcome }) {
  const { services, text, outcome: o } = props;
  const [open, setOpen] = useState(false);
  const report = o.kind === 'project' || o.kind === 'merged' ? o.report : null;
  const counts = report === null ? null : classCounts(report.entries);
  return (
    <li className={s.outcome} data-kind={o.kind}>
      <div className={s.outcomeHead}>
        <Icon name={OUTCOME_ICONS[o.kind]} size={16} />
        <div className={s.outcomeText}>
          <span className={s.outcomeName}>{o.job.name}</span>
          <span>{outcomeTitle(text, o)}</span>
          {report !== null && <span className={s.muted}>{reportSummary(text, report)}</span>}
          {counts !== null && (
            <span className={s.muted}>
              {text.t(m.classCounts, {
                changed: counts.changed,
                normalized: counts.normalized,
                flagged: counts.flagged,
              })}
            </span>
          )}
          {report !== null && !report.phiraWouldLoad && <span className={s.muted}>{text.t(m.phiraRefuses)}</span>}
          {o.kind === 'failed' && o.detail !== undefined && <span className={s.muted}>{o.detail}</span>}
        </div>
        <div className={s.outcomeActions}>
          {o.kind === 'project' && (
            <Button
              size="sm"
              variant="primary"
              icon="FolderOpen"
              onClick={() => {
                importQueue.closeSummary();
                void services.nav.openProject(o.projectId);
              }}
            >
              {text.t(m.openProject)}
            </Button>
          )}
          {report !== null && (
            <Button
              size="sm"
              icon={open ? 'ChevronUp' : 'ChevronDown'}
              aria-expanded={open}
              onClick={() => {
                setOpen(!open);
              }}
            >
              {text.t(m.showReport)}
            </Button>
          )}
          {o.kind === 'media' && !o.started && (
            <Button
              size="sm"
              icon="Sparkles"
              onClick={() => {
                importQueue.closeSummary();
                void startWizard(services, o.assets);
              }}
            >
              {text.t(m.startChart)}
            </Button>
          )}
        </div>
      </div>
      {open && report !== null && (
        <div className={s.details}>
          <ReportView
            text={text}
            report={report}
            missing={o.kind === 'project' || o.kind === 'merged' ? o.missing : 0}
            headingLevel={4}
          />
        </div>
      )}
    </li>
  );
}

export function ImportSummary(props: {
  readonly services: AppServices;
  readonly text: Text;
  readonly outcomes: readonly Outcome[];
}) {
  const { services, text, outcomes } = props;
  const close = (): void => {
    importQueue.closeSummary();
  };
  return (
    <Dialog
      open
      size="lg"
      title={text.t(m.summaryTitle)}
      description={text.t(m.summaryIntro, { count: outcomes.length })}
      onClose={close}
      footer={
        <div className={s.footer}>
          <div className={s.footerStart}>
            <ReportActions
              services={services}
              text={text}
              name={text.t(m.summaryTitle)}
              content={() => summaryText(text, outcomes)}
            />
          </div>
          <Button variant="primary" onClick={close}>
            {text.t(m.close)}
          </Button>
        </div>
      }
    >
      <div className={s.dialog}>
        <ul className={s.outcomes} aria-label={text.t(m.outcomeList)}>
          {outcomes.map((o) => (
            <OutcomeRow key={o.job.id} services={services} text={text} outcome={o} />
          ))}
        </ul>
        <h3 className={s.classTitle}>{text.t(m.importMore)}</h3>
        <DropZone
          text={text}
          onChooseFiles={() => {
            void services.registry.runAny('file.import', undefined, 'button');
          }}
          onChooseFolder={() => {
            void services.registry.runAny('file.importFolder', undefined, 'button');
          }}
        />
      </div>
    </Dialog>
  );
}

function RunningBanner(props: { readonly services: AppServices; readonly text: Text; readonly q: QueueSnapshot }) {
  const { services, text, q } = props;
  const cur = q.current;
  if (cur === null) return null;
  const overall = q.total > 0 ? (cur.index - 1 + (cur.progress ?? 0)) / q.total : null;
  const title = cur.waiting
    ? text.t(m.bannerWaiting, { name: cur.job.name })
    : q.total > 1
      ? text.t(m.bannerRunning, { index: cur.index, total: q.total, name: cur.job.name })
      : text.t(m.bannerRunningOne, { name: cur.job.name });
  return (
    <div className={s.banner} role="status" data-state="running">
      <Icon name="FolderInput" size={20} />
      <div className={s.bannerText}>
        <span className={s.bannerTitle}>{title}</span>
        <ProgressBar value={overall} label={{ key: m.progressLabel }} />
      </div>
      {q.total > 1 && (
        <div className={s.bannerActions}>
          <Button
            size="sm"
            variant="ghost"
            icon="X"
            aria-label={text.t(m.cancelImports)}
            onClick={() => {
              importQueue.cancel(services);
            }}
          >
            {text.t(m.cancel)}
          </Button>
        </div>
      )}
    </div>
  );
}

function FinishedBanner(props: { readonly text: Text; readonly q: QueueSnapshot }) {
  const { text, q } = props;
  return (
    <div className={s.banner} role="status" data-state="finished">
      <Icon name={failedCount(q.outcomes) > 0 ? 'TriangleAlert' : 'CircleCheck'} size={20} />
      <div className={s.bannerText}>
        <span className={s.bannerTitle}>
          {text.t(m.bannerDone, { created: createdCount(q.outcomes), failed: failedCount(q.outcomes) })}
        </span>
      </div>
      <div className={s.bannerActions}>
        <Button
          size="sm"
          icon="ListChecks"
          onClick={() => {
            importQueue.openSummary();
          }}
        >
          {text.t(m.viewReport)}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            importQueue.dismissBanner();
          }}
        >
          {text.t(m.dismiss)}
        </Button>
      </div>
    </div>
  );
}

/** The 'banner' status item: queue progress and the finished banner. */
export function ImportBanner(props: { readonly services: AppServices }) {
  const { services } = props;
  const text = useText(services);
  const q = useQueue();
  const store = useStore(services);
  const showRunning = q.running && q.current !== null && (q.total > 1 || store !== null || q.current.waiting);
  return (
    <>
      {showRunning && <RunningBanner services={services} text={text} q={q} />}
      {!q.running && q.finishedBanner && <FinishedBanner text={text} q={q} />}
    </>
  );
}

/**
 * The queue's dialogs (FeatureModule.overlays: mounted by the shell on every route): the batch-export picker and the
 * summary report. While one is mounted the queue asks with its picker; without (tests that render no shell) it falls
 * back to the system choice dialog.
 */
export function ImportQueueDialogs(props: { readonly services: AppServices }) {
  const { services } = props;
  const text = useText(services);
  const q = useQueue();
  const [token] = useState(() => importQueue.hostToken());
  useEffect(() => importQueue.attachHost(token), [token]);
  const primary = q.primaryHost === token;
  return (
    <>
      {primary && q.prompt !== null && (
        <BatchPicker
          text={text}
          prompt={q.prompt}
          onDone={(picked) => {
            importQueue.answerPrompt(picked);
          }}
        />
      )}
      {primary && q.summaryOpen && !q.running && (
        <ImportSummary services={services} text={text} outcomes={q.outcomes} />
      )}
    </>
  );
}
